import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../Navigations/Sidebar";
import TransactionList from "./TransactionList";
import TransactionApiClient from "../../services/TransactionApiClient";
import AddTransaction from "./AddTransaction";
import usePageTitle from "../../hooks/usePageTitle";
import { Transaction } from "../../types";

const Transactions: React.FC = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  });
  const [isAddTransactionOpen, setIsAddTransactionOpen] = useState(false);
  const lastCallParamsRef = useRef<string>("");

  usePageTitle("Transactions");

  const saleFilterValue = "sale";
  const refundFilterValue = "refund";

  const fetchTransactions = async (
    businessId: string,
    dateStart: string,
    dateEnd: string
  ) => {
    if (!businessId) return;

    setLoading(true);
    try {
      // Convert from YYYY-MM-DD to MM/DD/YYYY format for API
      const formatDateForAPI = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };
      console.log("Date range before parse:", dateStart, "to", dateEnd);
      const apiStartDate = formatDateForAPI(dateStart);
      const apiEndDate = formatDateForAPI(dateEnd);

      console.log("Fetching transactions for business:", businessId);
      console.log("Date range:", apiStartDate, "to", apiEndDate);

      const transactionData = await TransactionApiClient.fetchTransactions(
        businessId,
        apiStartDate,
        apiEndDate
      );

      console.log("Fetched transactions:", transactionData);
      setTransactions(transactionData);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshTransactions = () => {
    if (user?.SelectedBusiness?.id) {
      fetchTransactions(user.SelectedBusiness.id, startDate, endDate);
    }
  };

  const handleDateFilterChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    // fetchTransactions will be called automatically by useEffect when state changes
  };

  const handleAddTransaction = () => {
    setIsAddTransactionOpen(true);
  };

  const handleCloseAddTransaction = () => {
    setIsAddTransactionOpen(false);
  };

  const handleSaveTransaction = () => {
    // Refresh transactions after adding
    if (user?.SelectedBusiness?.id) {
      fetchTransactions(user.SelectedBusiness.id, startDate, endDate);
    }
    setIsAddTransactionOpen(false);
  };

  useEffect(() => {
    if (user?.SelectedBusiness?.id && !loading) {
      // Create a unique key for this API call
      const callKey = `${user.SelectedBusiness.id}-${startDate}-${endDate}`;
      
      // Only make the call if parameters have changed
      if (lastCallParamsRef.current !== callKey) {
        lastCallParamsRef.current = callKey;
        fetchTransactions(user.SelectedBusiness.id, startDate, endDate);
      }
    }
  }, [user?.SelectedBusiness?.id, startDate, endDate, loading]);

  const totalSales = transactions
    .filter((t) => t.transType === saleFilterValue)
    .reduce((sum, t) => sum + t.amount, 0);

  const totalRefund = transactions
    .filter((t) => t.transType === refundFilterValue)
    .reduce((sum, t) => sum + Math.abs(t.amount), 0);

  const balance = totalSales - totalRefund;

  return (
    <div className="app-layout">
      <Sidebar currentPage="/transactions" />

      <div className="main-content">
        <div className="page-header">
          <h2>📊 Transactions</h2>
          <p>
            Payment transactions for business {user?.SelectedBusiness?.name}
          </p>
        </div>
        <TransactionList
          transactions={transactions}
          loading={loading}
          onRefresh={refreshTransactions}
          startDate={startDate}
          endDate={endDate}
          onDateFilterChange={handleDateFilterChange}
          onAddTransaction={handleAddTransaction}
        />

        <AddTransaction
          isOpen={isAddTransactionOpen}
          businessId={user?.SelectedBusiness?.id || ""}
          onClose={handleCloseAddTransaction}
          onSave={handleSaveTransaction}
        />
      </div>
    </div>
  );
};

export default Transactions;
